import { fetchJsonWithRetry } from './httpRetry';

// Horaires RÉELS de l'atelier, calculés par le portail jour par jour (présences, jours fériés, samedis ouverts
// par l'interrupteur de l'écran Atelier) — même source que meaux-informatique.fr (chantier portail #20889, 21/10/2026).
const API_BASE = process.env.REACT_APP_PORTAIL_API_URL || 'https://support.meteris.fr';
export const URL_HORAIRES = `${API_BASE}/api/public/it-bookings/horaires`;

let enCours = null;
export function fetchHoraires() {
  if (!enCours) {
    enCours = fetchJsonWithRetry(URL_HORAIRES).catch((err) => {
      enCours = null;
      throw err;
    });
  }
  return enCours;
}

const LOCALES = { fr: 'fr-FR', en: 'en-GB', es: 'es-ES', de: 'de-DE', it: 'it-IT', pt: 'pt-PT' };

export function dateLocale(iso) {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d);
}

export function isoLocal(date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

// Heure au format de chaque langue, comme le reste du site : FR « 16h30 » / « 18h », EN « 4:30 PM », autres « 16:30 ».
export function heure(hhmm, lang = 'fr') {
  const [h, m] = String(hhmm).split(':').map(Number);
  if (lang === 'fr') return m ? `${h}h${String(m).padStart(2, '0')}` : `${h}h`;
  if (lang === 'en') {
    const h12 = h % 12 === 0 ? 12 : h % 12;
    return `${h12}${m ? `:${String(m).padStart(2, '0')}` : ''} ${h < 12 ? 'AM' : 'PM'}`;
  }
  return `${h}:${String(m).padStart(2, '0')}`;
}

export function plagesTexte(plages, lang = 'fr') {
  const texte = (plages || []).map(([debut, fin]) => `${heure(debut, lang)} – ${heure(fin, lang)}`).join(' / ');
  return lang === 'de' && texte ? `${texte} Uhr` : texte;
}

function majuscule(t) {
  return t.charAt(0).toUpperCase() + t.slice(1);
}

export function jourLong(iso, lang = 'fr') {
  return majuscule(new Intl.DateTimeFormat(LOCALES[lang] || 'fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })
    .format(dateLocale(iso)));
}

export function jourCourt(iso, lang = 'fr') {
  return new Intl.DateTimeFormat(LOCALES[lang] || 'fr-FR', { weekday: 'short', day: '2-digit', month: '2-digit' })
    .format(dateLocale(iso));
}

function estSamedi(iso) {
  return dateLocale(iso).getDay() === 6;
}

export function ouvert(jour) {
  return Boolean(jour) && !jour.ferme && (jour.sans_rdv || []).length > 0;
}

function hhmmDe(now) {
  return `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
}

export function joursAVenir(data, now = new Date()) {
  const aujourdhui = isoLocal(now);
  return (data?.jours || []).filter((j) => j.date >= aujourdhui);
}

function samediEncoreAVenir(j, now) {
  if (j.date !== isoLocal(now) || !ouvert(j)) return true;
  return j.sans_rdv[j.sans_rdv.length - 1][1] > hhmmDe(now);
}

export function aujourdhui(data, now = new Date()) {
  return (data?.jours || []).find((j) => j.date === isoLocal(now)) || null;
}

export function samedisAVenir(data, now = new Date(), nombre = 4) {
  return joursAVenir(data, now).filter((j) => estSamedi(j.date) && samediEncoreAVenir(j, now)).slice(0, nombre);
}

export function etatSamedi(data, now = new Date()) {
  const samedis = samedisAVenir(data, now, 99);
  if (!samedis.length) return null;
  const premier = samedis[0];
  if (ouvert(premier)) return { samedi: premier, ouvert: true, prochainOuvert: null };
  return { samedi: premier, ouvert: false, prochainOuvert: samedis.slice(1).find(ouvert) || null };
}

export function prochaineOuverture(data, now = new Date()) {
  const hhmm = hhmmDe(now);
  for (const j of joursAVenir(data, now)) {
    if (!ouvert(j)) continue;
    const plage = j.date === isoLocal(now) ? j.sans_rdv.find(([debut]) => debut > hhmm) : j.sans_rdv[0];
    if (plage) return { date: j.date, debut: plage[0] };
  }
  return null;
}

// Textes courts dans les 6 langues du site (le reste de la page vit dans i18n/translations.js).
export const MOTS = {
  ouvertMaintenant: { fr: 'Ouvert maintenant', en: 'Open now', es: 'Abierto ahora', de: 'Jetzt geöffnet', it: 'Aperto ora', pt: 'Aberto agora' },
  jusqua: { fr: "jusqu'à", en: 'until', es: 'hasta las', de: 'bis', it: 'fino alle', pt: 'até às' },
  fermeReouverture: { fr: 'Fermé — réouverture', en: 'Closed — reopens', es: 'Cerrado — reapertura', de: 'Geschlossen — wieder geöffnet', it: 'Chiuso — riapertura', pt: 'Fechado — reabertura' },
  fermePourLeMoment: { fr: 'Fermé pour le moment', en: 'Closed for now', es: 'Cerrado por el momento', de: 'Derzeit geschlossen', it: 'Chiuso per il momento', pt: 'Fechado de momento' },
  aujourdhui: { fr: "aujourd'hui", en: 'today', es: 'hoy', de: 'heute', it: 'oggi', pt: 'hoje' },
  demain: { fr: 'demain', en: 'tomorrow', es: 'mañana', de: 'morgen', it: 'domani', pt: 'amanhã' },
  a: { fr: 'à', en: 'at', es: 'a las', de: 'um', it: 'alle', pt: 'às' },
  ferme: { fr: 'Fermé', en: 'Closed', es: 'Cerrado', de: 'Geschlossen', it: 'Chiuso', pt: 'Fechado' },
  jourFerie: { fr: 'jour férié', en: 'public holiday', es: 'festivo', de: 'Feiertag', it: 'festivo', pt: 'feriado' },
  exceptionnel: { fr: 'exceptionnellement', en: 'exceptionally', es: 'excepcionalmente', de: 'ausnahmsweise', it: 'eccezionalmente', pt: 'excecionalmente' },
};

// Accueil TÉLÉPHONIQUE (décision William du 23/10/2026) : distinct de l'accueil à l'atelier.
export const PLAGES_TELEPHONE_DEFAUT = [['09:00', '12:00'], ['14:00', '17:00']];
const MOTS_TEL = {
  joignable: { fr: 'Joignable maintenant', en: 'Available now', es: 'Disponible ahora', de: 'Jetzt erreichbar', it: 'Raggiungibili ora', pt: 'Disponível agora' },
  reprise: { fr: 'Fermé — reprise', en: 'Closed — back', es: 'Cerrado — reanudación', de: 'Geschlossen — wieder erreichbar', it: 'Chiuso — ripresa', pt: 'Fechado — retoma' },
};

export function plagesTelephone(data, lang = 'fr') {
  return plagesTexte(data?.semaine_type?.telephone || PLAGES_TELEPHONE_DEFAUT, lang);
}

function prochainTelephone(data, now) {
  const hhmm = hhmmDe(now);
  for (const j of joursAVenir(data, now)) {
    const plages = j.telephone || [];
    const plage = j.date === isoLocal(now) ? plages.find(([debut]) => debut > hhmm) : plages[0];
    if (plage) return { date: j.date, debut: plage[0] };
  }
  return null;
}

export function statutTelephone(data, lang = 'fr', now = new Date()) {
  const t = (cle) => MOTS_TEL[cle][lang] || MOTS_TEL[cle].fr;
  if (data?.maintenant_telephone?.ouvert) {
    const fin = data.maintenant_telephone.jusqu_a;
    return { ouvert: true, texte: fin ? `${t('joignable')} ${mot('jusqua', lang)} ${heure(fin, lang)}` : t('joignable') };
  }
  const suite = prochainTelephone(data, now);
  if (!suite) return { ouvert: false, texte: mot('fermePourLeMoment', lang) };
  const demain = isoLocal(new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1));
  const quand = suite.date === isoLocal(now) ? mot('aujourdhui', lang)
    : suite.date === demain ? mot('demain', lang) : jourLong(suite.date, lang);
  return { ouvert: false, texte: `${t('reprise')} ${quand} ${mot('a', lang)} ${heure(suite.debut, lang)}` };
}

export function mot(cle, lang) {
  return MOTS[cle][lang] || MOTS[cle].fr;
}

export function texteJour(jour, lang = 'fr') {
  if (!jour) return '';
  if (!ouvert(jour)) {
    if (jour.note === 'Jour férié') return `${mot('ferme', lang)} (${mot('jourFerie', lang)})`;
    if (jour.note) return `${mot('ferme', lang)} (${mot('exceptionnel', lang)})`;
    return mot('ferme', lang);
  }
  return plagesTexte(jour.sans_rdv, lang);
}

export function statutMaintenant(data, lang = 'fr', now = new Date()) {
  if (data?.maintenant?.ouvert) {
    const fin = data.maintenant.jusqu_a;
    return { ouvert: true, texte: fin ? `${mot('ouvertMaintenant', lang)} ${mot('jusqua', lang)} ${heure(fin, lang)}` : mot('ouvertMaintenant', lang) };
  }
  const suite = prochaineOuverture(data, now);
  if (!suite) return { ouvert: false, texte: mot('fermePourLeMoment', lang) };
  const demain = isoLocal(new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1));
  const quand = suite.date === isoLocal(now) ? mot('aujourdhui', lang)
    : suite.date === demain ? mot('demain', lang) : jourLong(suite.date, lang);
  return { ouvert: false, texte: `${mot('fermeReouverture', lang)} ${quand} ${mot('a', lang)} ${heure(suite.debut, lang)}` };
}
